/**
 * iter 53 / 69 — UNIFIED DEPARTMENT MANAGER DRAWER (multi-manager).
 *
 * Canonical drawer for Department create + edit anywhere in the app.
 * Endpoints:
 *   PATCH /api/org-admin/{orgId}/departments/{did}
 *   POST  /api/org-admin/{orgId}/departments
 *   PUT   /api/org-admin/{orgId}/departments/{did}/managers   (multi-manager)
 */
import { toast } from "sonner";
import { UsersThree } from "@phosphor-icons/react";
import { api, formatErr } from "../../lib/api";
import { Drawer, Field } from "../UI";
import {
  ErrorBanner, DrawerFooter, useDrawerForm,
} from "./_managerShared";

const EMPTY = { name: "", description: "", manager_user_ids: [] };

const hydrate = (rec) => ({
  name: rec.name || "",
  description: rec.description || "",
  // Backward-compatible: prefer manager_user_ids if present, else legacy single.
  manager_user_ids: rec.manager_user_ids
    || (rec.manager_user_id ? [rec.manager_user_id] : []),
});

export default function DepartmentManagerDrawer({
  open, onClose, onSuccess, mode = "create", initialData = null,
  orgId, orgName, users = [],
}) {
  const isEdit = mode === "edit";
  const f = useDrawerForm({ open, isEdit, initialData, emptyForm: EMPTY, hydrate });
  const { form, setForm, setField, busy, setBusy, err, setErr, requestClose } = f;

  const toggleManager = (uid) => {
    setForm((cur) => ({
      ...cur,
      manager_user_ids: cur.manager_user_ids.includes(uid)
        ? cur.manager_user_ids.filter((x) => x !== uid)
        : [...cur.manager_user_ids, uid],
    }));
  };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const corePayload = {
        name: form.name,
        description: form.description || null,
      };
      let saved;
      if (isEdit) {
        const did = initialData.id;
        const r = await api.patch(`/org-admin/${orgId}/departments/${did}`, corePayload);
        await api.put(`/org-admin/${orgId}/departments/${did}/managers`, {
          user_ids: form.manager_user_ids,
        });
        saved = r.data;
      } else {
        // POST accepts manager_user_ids inline (single round-trip).
        const r = await api.post(`/org-admin/${orgId}/departments`, {
          ...corePayload,
          manager_user_ids: form.manager_user_ids,
        });
        saved = r.data;
      }
      toast.success(isEdit ? `Department "${form.name}" updated.` : `Department "${form.name}" added.`);
      onSuccess?.(saved);
      onClose?.();
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!isEdit || !initialData) return;
    if (!confirm(`Delete department "${initialData.name}"? Will be blocked if any user is assigned.`)) return;
    setBusy(true); setErr("");
    try {
      await api.delete(`/org-admin/${orgId}/departments/${initialData.id}`);
      toast.success(`Department "${initialData.name}" deleted.`);
      onSuccess?.(null);
      onClose?.();
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  // Members of this department (via the new multi-dept join).
  const usersInDept = isEdit && initialData
    ? users.filter((u) => (u.department_ids || []).includes(initialData.id))
    : [];

  return (
    <Drawer
      open={open}
      onClose={() => requestClose(onClose)}
      title={isEdit ? "Edit department" : "Add department"}
      size="narrow"
      testid="department-manager-drawer"
      footer={
        <DrawerFooter
          testid="department-manager-footer"
          onCancel={() => requestClose(onClose)}
          onSave={save}
          mode={mode}
          busy={busy}
          saveLabel={isEdit ? "Update department" : "Create department"}
          saveDisabled={!form.name.trim()}
          left={isEdit && (
            <button
              type="button"
              className="btn btn-ghost text-destructive"
              onClick={remove}
              disabled={busy}
              data-testid="department-manager-delete-btn"
            >
              DELETE
            </button>
          )}
        />
      }
    >
      <ErrorBanner err={err} testid="department-manager-error" />

      <Field label="DEPARTMENT NAME">
        <input className="input" autoFocus value={form.name}
          onChange={(e) => setField("name", e.target.value)}
          placeholder="e.g. Process Serving"
          data-testid="department-manager-name-input" />
      </Field>

      <Field label="DESCRIPTION">
        <textarea className="input" rows={3} value={form.description}
          onChange={(e) => setField("description", e.target.value)}
          placeholder="What does this department do?"
          data-testid="department-manager-description-input" />
      </Field>

      <Field label={`MANAGERS (${form.manager_user_ids.length} selected)`}>
        <div className="surface p-2 max-h-48 overflow-y-auto" data-testid="department-manager-managers-checklist">
          {users.length === 0 && (
            <div className="text-xs text-muted-foreground italic">No users yet.</div>
          )}
          {users.map((u) => (
            <label key={u.id} className="flex items-center gap-2 py-1 px-1 hover:bg-muted/40 cursor-pointer rounded">
              <input
                type="checkbox"
                checked={form.manager_user_ids.includes(u.id)}
                onChange={() => toggleManager(u.id)}
                data-testid={`department-manager-checkbox-${u.id}`}
              />
              <span className="text-sm flex-1 truncate">{u.name}</span>
              <span className="text-[10px] text-muted-foreground truncate">{u.email}</span>
            </label>
          ))}
        </div>
      </Field>

      {isEdit && (
        <div className="mt-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1">
            <UsersThree size={12} /> Users in this department ({usersInDept.length})
          </div>
          {usersInDept.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">No users assigned.</div>
          ) : (
            <div className="surface p-2 space-y-1" data-testid="department-manager-users-list">
              {usersInDept.map((u) => (
                <div key={u.id} className="text-xs flex items-center justify-between">
                  <span className="font-medium">{u.name}</span>
                  <span className="text-muted-foreground">{u.role}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}
